import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import RegistrationWizard from './pages/RegistrationWizard';
import SetPassword from './pages/SetPassword';
import SuperAdminLayout from './pages/SuperAdminLayout';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TenantList from './pages/TenantList';
import CreateTenant from './pages/CreateTenant';
import AccessRequests from './pages/AccessRequests';
import AppLayout from './pages/AppLayout';
import AppDashboard from './pages/AppDashboard';
import DocumentList from './pages/DocumentList';
import DocumentDetail from './pages/DocumentDetail';
import UploadDocument from './pages/UploadDocument';
import UsersPage from './pages/UsersPage';
import { CategoriesPage, AuditLogsPage } from './pages/OtherPages';

// Guards
const RequireAuth = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role === 'super_admin' && user.type !== 'super_admin') return <Navigate to="/app" replace />;
  if (role === 'tenant' && user.type === 'super_admin') return <Navigate to="/super-admin" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<RegistrationWizard />} />
      <Route path="/set-password" element={<SetPassword />} />

      {/* Super Admin */}
      <Route path="/super-admin" element={
        <RequireAuth role="super_admin"><SuperAdminLayout /></RequireAuth>
      }>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<TenantList />} />
        <Route path="access-requests" element={<AccessRequests />} />
        <Route path="tenants/new" element={<CreateTenant />} />
        <Route path="tenants/:id" element={<CreateTenant mode="view" />} />
      </Route>

      {/* Tenant App */}
      <Route path="/app" element={
        <RequireAuth role="tenant"><AppLayout /></RequireAuth>
      }>
        <Route index element={<AppDashboard />} />
        <Route path="documents" element={<DocumentList />} />
        <Route path="documents/upload" element={<UploadDocument />} />
        <Route path="documents/:id" element={<DocumentDetail />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="categories/:categoryId" element={<DocumentList />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="audit" element={<AuditLogsPage />} />
      </Route>

      {/* Root redirect */}
      <Route path="/" element={
        user ? (user.type === 'super_admin' ? <Navigate to="/super-admin" /> : <Navigate to="/app" />) : <Navigate to="/login" />
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { fontSize: '14px', borderRadius: '10px', fontFamily: 'Inter, sans-serif' },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
